import { Modal } from '@farol-ds/react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { useGA4Events } from '../../../hooks'
import { CpfValidation_ViewerFragment } from '../../cpf-validation/__generated__/cpf-validation.graphql'
import { ModalContextProvider } from '../../cpf-validation/context'
import { Steps } from '../../cpf-validation/enums'
import { StepRenderer } from '../../cpf-validation/step-renderer'
import { COMPLETE_SIGNUP_NUDGE_ORIGIN } from '../constants'
import focusStyles from '../hide-focus-visible.module.scss'
import { useFocusRingSuppressedUntilTab } from '../use-focus-ring-suppressed-until-tab'
import styles from './complete-signup-modal.module.scss'
import { CpfDobStep } from './cpf-dob-step'
import { NUDGE_CLOSE_CLICK_PROPS } from './ga4-events'
import { NudgeValidatingStep } from './nudge-validating-step'
import { PhoneStep } from './phone-step'
import { SuccessStep } from './success-step'

const PHONE_STEP = 'phone' as const
type ModalStep = Steps | typeof PHONE_STEP

const CLOSE_PROPS_BY_STEP: Partial<Record<ModalStep, typeof NUDGE_CLOSE_CLICK_PROPS.CPF_DOB>> = {
  [Steps.VALIDATE_IDENTITY]: NUDGE_CLOSE_CLICK_PROPS.CPF_DOB,
  [PHONE_STEP]: NUDGE_CLOSE_CLICK_PROPS.PHONE,
  [Steps.IDENTITY_SUCCESS]: NUDGE_CLOSE_CLICK_PROPS.SUCCESS,
}

interface CompleteSignupModalProps {
  open: boolean
  viewer: CpfValidation_ViewerFragment | null
  firstName?: string | null
  skipPhoneStep?: boolean
  /** Closing via X / esc / outside — the orchestrator snoozes and closes. */
  onDismiss: () => void
  /** Skip CTA ("preencher depois") — the orchestrator snoozes and closes. */
  onSkip: () => void
  /** Completion ("concluir") — just closes. */
  onClose: () => void
}

export function CompleteSignupModal({
  open,
  viewer,
  firstName,
  skipPhoneStep = false,
  onDismiss,
  onSkip,
  onClose,
}: CompleteSignupModalProps) {
  const [step, setStep] = useState<ModalStep>(Steps.VALIDATE_IDENTITY)
  const ga4 = useGA4Events()
  const suppressFocusRing = useFocusRingSuppressedUntilTab()

  // Reset to the first step whenever the modal reopens (the orchestrator may keep it mounted).
  useEffect(() => {
    if (open) setStep(Steps.VALIDATE_IDENTITY)
  }, [open])

  // Closing via X / esc / outside all funnel through Radix's onOpenChange(false); programmatic
  // closes (skip/complete) set `open` directly and never reach it, so no double counting.
  function handleDismiss() {
    ga4.sendClick(CLOSE_PROPS_BY_STEP[step] ?? NUDGE_CLOSE_CLICK_PROPS.IDENTITY_VALIDATION)
    onDismiss()
  }

  function handleSetStep(next: Steps) {
    if (next === Steps.IDENTITY_SUCCESS && !skipPhoneStep) {
      setStep(PHONE_STEP)
      return
    }
    setStep(next)
  }

  function renderStep() {
    switch (step) {
      case Steps.VALIDATE_IDENTITY:
        return <CpfDobStep firstName={firstName} onSkip={onSkip} />
      case Steps.VALIDATING_IDENTITY:
        return <NudgeValidatingStep />
      case PHONE_STEP:
        return (
          <PhoneStep
            onActivated={() => setStep(Steps.IDENTITY_SUCCESS)}
            onSkip={() => setStep(Steps.IDENTITY_SUCCESS)}
          />
        )
      case Steps.IDENTITY_SUCCESS:
        return <SuccessStep onClose={onClose} />
      default:
        return <StepRenderer currentStep={step} onClose={onClose} />
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) handleDismiss()
      }}
      size="sm"
    >
      <Modal.Content
        containerClassName={classNames(
          { [styles.squareContent]: step === Steps.IDENTITY_SUCCESS },
          { [focusStyles.hideFocusVisible]: suppressFocusRing }
        )}
      >
        <ModalContextProvider
          setCurrentStep={handleSetStep}
          viewer={viewer}
          validationOrigin={COMPLETE_SIGNUP_NUDGE_ORIGIN}
          eventData={{ from_component: 'complete_signup_nudge' }}
          replacementToken={null}
        >
          <Modal.IconClose />
          {renderStep()}
        </ModalContextProvider>
      </Modal.Content>
    </Modal>
  )
}
